import React, { useMemo, Fragment, useState, useCallback } from "react";

import { AxisLeft } from "@visx/axis";
import { localPoint } from "@visx/event";
import { GridRows } from "@visx/grid";
import { Group } from "@visx/group";
import { scaleBand, scaleLinear } from "@visx/scale";
import { Bar, LinePath } from "@visx/shape";
import { useTooltip, useTooltipInPortal } from "@visx/tooltip";
import { find } from "lodash/fp";

import { Stack } from "@mui/material";

import {
  getFillColor,
  getSeriesRange,
  getXYPointsFromSeries,
  scaleBandInvertIndex,
} from "./chartUtils";
import LabelText from "./LabelText";
import { CommonBarChartProps } from "./types";

export type VerticalBarChartProps = CommonBarChartProps<"vertical">;

const DEFAULT_PADDING = 0.4;

const VerticalBarChart = ({
  width,
  height,
  series,
  layout,
}: VerticalBarChartProps & { width: number; height: number }) => {
  const { tooltipOpen, tooltipLeft, tooltipTop, hideTooltip, showTooltip } =
    useTooltip<{ text: string }>();

  const { containerRef, containerBounds, TooltipInPortal } = useTooltipInPortal(
    {
      detectBounds: true,
    }
  );

  const [hoveredSeriesIndex, setHoveredSeriesIndex] = useState<number | null>(
    null
  );

  const margin = { top: 0, right: 0, bottom: 8, left: 60 };

  // bounds
  const xMax = Math.max(width - margin.left - margin.right, 0);
  const yMax = Math.max(height - margin.top - margin.bottom - 34, 0);

  const barSeries = find({ type: "bar" }, series);
  const lineSeries = find({ type: "line" }, series);
  const textSeries = find({ type: "text" }, series);

  const { min: seriesMin, max: seriesMax } = getSeriesRange([
    ...(barSeries?.y ?? []),
    ...(lineSeries?.y ?? []),
  ]);

  const numTicks = useMemo(() => {
    if (layout?.yAxis?.numTicks) {
      return layout.yAxis.numTicks;
    } else if (layout?.yAxis?.tickEvery) {
      const range = seriesMax - seriesMin;
      return Math.round(range / layout.yAxis.tickEvery);
    }
    return undefined;
  }, [layout?.yAxis?.numTicks, layout?.yAxis?.tickEvery, seriesMin, seriesMax]);

  // scales, memoize for performance
  const xScale = useMemo(
    () =>
      scaleBand<string>({
        range: [0, xMax],
        round: true,
        domain: series[0]?.x ?? [],
        padding: DEFAULT_PADDING,
      }),
    [xMax]
  );

  // TODO: calculate domain and support override in props
  const yScale = useMemo(
    () =>
      scaleLinear<number>({
        range: [yMax, 0],
        round: true,
        domain: [seriesMin, seriesMax],
      }),
    [yMax, seriesMin, seriesMax]
  );

  const handleTooltip = (
    event: React.TouchEvent<SVGGElement> | React.MouseEvent<SVGGElement>
  ) => {
    const { x } = localPoint(event) || { x: 0 };
    const left =
      ("clientX" in event ? event.clientX : 0) - containerBounds.left;
    const top =
      ("clientY" in event ? event.clientY : 0) - containerBounds.top + 10;
    const index = scaleBandInvertIndex(xScale, x - margin.left);

    showTooltip({
      tooltipTop: top,
      tooltipLeft: left,
    });
    setHoveredSeriesIndex(index);
  };

  const handleTooltipLeave = useCallback(() => {
    hideTooltip();
    setHoveredSeriesIndex(null);
  }, []);

  return (
    <>
      <svg
        ref={containerRef}
        width={width}
        height={height}
        style={{ cursor: "pointer" }}
      >
        <Group left={margin.left} top={margin.top}>
          {barSeries?.x.map((d, i) => {
            const barWidth = xScale.bandwidth();
            const barHeight = 30;
            const barX = xScale(d) ?? 0;
            return (
              <Fragment key={`bar-head-${d}`}>
                <Bar
                  key={`bar-${d}`}
                  x={barX - (barWidth * DEFAULT_PADDING) / 2}
                  y={0}
                  rx={6}
                  width={barWidth + barWidth * DEFAULT_PADDING}
                  height={barHeight}
                  fill="#F5F6FA"
                />
                <LabelText
                  x={barX + barWidth / 2}
                  y={20}
                  width={barWidth}
                  fontSize={14}
                >
                  {d}
                </LabelText>
                {i < barSeries.x.length - 1 && !layout?.xAxis?.hideLines && (
                  <line
                    x1={barX + barWidth + barWidth * 0.3}
                    x2={barX + barWidth + barWidth * 0.3}
                    y1={0}
                    y2={34}
                    stroke="#E0E0E0"
                  />
                )}
              </Fragment>
            );
          })}
        </Group>
        <Group left={margin.left} top={margin.top + 34}>
          <GridRows
            scale={yScale}
            width={xMax}
            height={yMax}
            tickValues={layout?.yAxis?.ticks}
            numTicks={numTicks}
            strokeDasharray="4"
            stroke="#E0E0E0"
          />
          <line x1={0} x2={xMax} y1={yMax} y2={yMax} stroke="#E0E0E0" />
          <line x1={0} x2={xMax} y1={0} y2={0} stroke="#E0E0E0" />
          <AxisLeft
            hideAxisLine
            hideTicks
            scale={yScale}
            tickValues={layout?.yAxis?.ticks}
            numTicks={numTicks}
            label={layout?.yAxis?.label}
            labelProps={{
              fill: "#848484",
              fontFamily: "'Work Sans', sans-serif",
              fontSize: 12,
              textAnchor: "middle",
              fontWeight: 500,
            }}
            tickStroke="#848484"
            tickFormat={(d) => {
              // hide fractional ticks
              if (Math.round(d.valueOf()) !== d.valueOf()) {
                return "";
              }
              return `${Math.round(d.valueOf())}`;
            }}
            tickComponent={({ x, y, formattedValue }) => {
              if (formattedValue === "") {
                return null;
              }
              return (
                <LabelText x={x - 8} y={y + 3} fill="#848484" fontSize={12}>
                  {formattedValue}
                </LabelText>
              );
            }}
          />
          {barSeries?.x.map((d, i) => {
            if (typeof barSeries.y[i] === "undefined") return null;

            const barWidth = xScale.bandwidth();
            const barHeight = yMax - yScale(barSeries.y[i] ?? 0);
            const barX = xScale(d) ?? 0;
            const barY = yMax - barHeight;

            return (
              <Group key={`bar-${d}`}>
                {!barSeries?.hideLabels && barSeries?.text?.[i] ? (
                  <LabelText
                    x={barX + barWidth / 2}
                    y={barY - 6}
                    width={barWidth}
                  >
                    {barSeries.text[i] ?? 0}
                  </LabelText>
                ) : null}
                <Bar
                  key={`bar-${d}`}
                  x={barX}
                  y={barY}
                  rx={6}
                  width={barWidth}
                  height={Math.max(barHeight, 0)}
                  fill={getFillColor(barSeries.fillColor, barSeries.y[i])}
                  strokeWidth={i === hoveredSeriesIndex ? 2 : undefined}
                  stroke={i === hoveredSeriesIndex ? "#EDEDFA" : undefined}
                />
                {textSeries?.y?.[i] && (
                  <LabelText
                    x={barX + barWidth / 2}
                    y={yMax - 8}
                    width={barWidth}
                    fill="#fff"
                    fontSize={14}
                  >
                    {textSeries.text?.[i] ?? textSeries.y[i] ?? ""}
                  </LabelText>
                )}
                {i < barSeries.x.length - 1 && !layout?.xAxis?.hideLines && (
                  <line
                    x1={barX + barWidth + barWidth * 0.3}
                    x2={barX + barWidth + barWidth * 0.3}
                    y1={0}
                    y2={yMax}
                    stroke="#E0E0E0"
                  />
                )}
              </Group>
            );
          })}
          {lineSeries && (
            <Group>
              <LinePath
                data={getXYPointsFromSeries(lineSeries)}
                x={(d) => (xScale(d.x) ?? 0) + xScale.bandwidth() / 2}
                y={(d) => yScale(d.y)}
                stroke="#4C47CC"
                strokeWidth={2}
                strokeDasharray={4}
              />
              {getXYPointsFromSeries(lineSeries).map((d, i) => {
                return (
                  <circle
                    key={i}
                    r={5}
                    cx={(xScale(d.x) ?? 0) + xScale.bandwidth() / 2}
                    cy={yScale(d.y)}
                    stroke="#4C47CC"
                    fill="#fff"
                    strokeWidth={i === hoveredSeriesIndex ? 4 : 2}
                  />
                );
              })}
            </Group>
          )}
          <Bar
            x={0}
            y={margin.top}
            width={xMax}
            height={yMax}
            fill="transparent"
            onTouchStart={handleTooltip}
            onTouchMove={handleTooltip}
            onMouseMove={handleTooltip}
            onMouseLeave={handleTooltipLeave}
          />
        </Group>
      </svg>
      {tooltipOpen && hoveredSeriesIndex != null && (
        <TooltipInPortal
          top={tooltipTop}
          left={tooltipLeft}
          unstyled
          applyPositionStyle
        >
          <Stack
            sx={{
              backgroundColor: "#000",
              color: "#fafafa",
              fontWeight: 500,
              borderRadius: "4px",
              padding: "6px 13px",
            }}
          >
            <ul
              style={{
                listStyle: "none",
                padding: 0,
                margin: 0,
                fontSize: "0.75rem",
              }}
            >
              {series.map((s) => {
                const formattedValue = s.text
                  ? s.text[hoveredSeriesIndex]?.replace(/\[|\]/g, "")
                  : s.y[hoveredSeriesIndex];
                return (
                  <li key={s.name}>
                    {s.name}: {formattedValue ?? "-"}
                  </li>
                );
              })}
            </ul>
          </Stack>
        </TooltipInPortal>
      )}
    </>
  );
};

export default VerticalBarChart;
